// ============================================================
// TeamSettings — /t/:slug/settings (owner-only)
// Uređivanje: ime, opis, logo URL, slug, visibility, soft password lock.
// Brisanje tima.
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import bcrypt from 'bcryptjs';
import {
  db,
  doc,
  collection,
  getDocs,
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  runTransaction,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  query,
  where,
} from '../firebase';
import { useTeam } from '../contexts/TeamContext';
import { normalizeEmail } from '../utils/membership';
import '../App.css';

/* eslint-disable jsx-a11y/accessible-emoji */

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 30);
};

const PASSWORD_MIN_LENGTH = 4;

const TeamSettings = () => {
  const { teamId, teamData, slug, loading } = useTeam();
  const history = useHistory();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logo, setLogo] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [newSlug, setNewSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [teamPassword, setTeamPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [confirmName, setConfirmName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [teamMembers, setTeamMembers] = useState([]);
  const [managerUids, setManagerUids] = useState([]);
  const [managerEmail, setManagerEmail] = useState('');
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerError, setManagerError] = useState('');
  const [managerSuccess, setManagerSuccess] = useState('');

  useEffect(() => {
    if (teamData) {
      setName(teamData.name || '');
      setDescription(teamData.description || '');
      setLogo(teamData.logo || '');
      setVisibility(teamData.visibility || 'public');
      setNewSlug(teamData.slug || slug || '');
      setPasswordEnabled(!!teamData.passwordEnabled);
      setManagerUids(teamData.managerUids || []);
    }
  }, [teamData, slug]);

  useEffect(() => {
    if (!teamId) return;
    getDocs(collection(db, 'teams', teamId, 'members')).then((snap) => {
      setTeamMembers(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((m) => !m.removed)
      );
    });
  }, [teamId]);

  const handleNameChange = (val) => {
    setName(val);
    if (!slugTouched) setNewSlug(slugify(val));
  };

  const handleSlugChange = (val) => {
    setSlugTouched(true);
    setNewSlug(slugify(val));
  };

  const [managerProfiles, setManagerProfiles] = useState({});

  useEffect(() => {
    if (managerUids.length === 0) return;
    managerUids.forEach(async (uid) => {
      if (managerProfiles[uid]) return;
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) {
          setManagerProfiles((prev) => ({ ...prev, [uid]: snap.data() }));
        }
      } catch (_) {}
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerUids]);

  const managers = useMemo(() => {
    return managerUids.map((uid) => {
      const member = teamMembers.find((tm) => tm.uid === uid);
      const profile = managerProfiles[uid];
      const label = member?.displayName || member?.email || profile?.displayName || profile?.email || uid;
      return { uid, label };
    });
  }, [managerUids, teamMembers, managerProfiles]);

  const handleAddManager = async () => {
    setManagerError('');
    setManagerSuccess('');
    const email = normalizeEmail(managerEmail.trim());
    if (!email) return;

    if (email === normalizeEmail(teamData?.ownerEmail || '')) {
      setManagerError('Vlasnik tima ne treba biti urednik.');
      return;
    }

    setManagerLoading(true);
    try {
      // Traži korisnika u users kolekciji po emailu
      const usersSnap = await getDocs(query(collection(db, 'users'), where('email', '==', email)));
      if (usersSnap.empty) {
        setManagerError('Korisnik s tim emailom nije registriran u aplikaciji.');
        return;
      }

      const userData = usersSnap.docs[0].data();
      const uid = userData.uid;

      if (!uid) {
        setManagerError('Korisnik nema valjan uid.');
        return;
      }
      if (managerUids.includes(uid)) {
        setManagerError('Ovaj korisnik je već urednik.');
        return;
      }

      await updateDoc(doc(db, 'teams', teamId), { managerUids: arrayUnion(uid) });
      setManagerUids((prev) => [...prev, uid]);
      setManagerEmail('');
      setManagerSuccess(`${userData.displayName || email} dodan kao urednik.`);
    } catch (err) {
      setManagerError('Greška: ' + err.message);
    } finally {
      setManagerLoading(false);
    }
  };

  const handleRemoveManager = async (uid) => {
    setManagerError('');
    setManagerSuccess('');
    try {
      await updateDoc(doc(db, 'teams', teamId), { managerUids: arrayRemove(uid) });
      setManagerUids((prev) => prev.filter((u) => u !== uid));
    } catch (err) {
      setManagerError('Greška: ' + err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Ime tima je obavezno.');
      return;
    }

    const cleanSlug = slugify(newSlug);
    if (!cleanSlug) {
      setError('Slug mora sadržavati slova ili brojeve.');
      return;
    }

    if (visibility === 'private' && !teamData.passwordEnabled) {
      setError('Prvo spremi lozinku tima, pa onda prebaci vidljivost na privatno.');
      return;
    }

    setSaving(true);
    try {
      const teamFields = {
        name: trimmedName,
        slug: cleanSlug,
        description: description.trim(),
        logo: logo.trim() || null,
        visibility,
        updatedAt: serverTimestamp(),
      };

      const slugFields = {
        teamId,
        ownerUid: teamData.ownerUid,
        teamName: trimmedName,
        visibility,
        passwordEnabled: !!teamData.passwordEnabled,
        createdAt: teamData.createdAt || serverTimestamp(),
      };

      if (cleanSlug !== teamData.slug) {
        const oldSlugRef = doc(db, 'slugs', teamData.slug);
        const newSlugRef = doc(db, 'slugs', cleanSlug);
        const teamRef = doc(db, 'teams', teamId);

        await runTransaction(db, async (tx) => {
          const newSlugSnap = await tx.get(newSlugRef);
          if (newSlugSnap.exists()) {
            throw new Error(`SLUG_TAKEN:${cleanSlug}`);
          }

          tx.delete(oldSlugRef);
          tx.set(newSlugRef, slugFields);
          tx.set(teamRef, teamFields, { merge: true });
        });

        localStorage.removeItem(`team-unlocked:${teamData.slug}`);
      } else {
        await setDoc(doc(db, 'teams', teamId), teamFields, { merge: true });
        await setDoc(doc(db, 'slugs', cleanSlug), slugFields, { merge: true });
      }

      setSuccess('✅ Postavke spremljene.');

      if (cleanSlug !== slug) {
        history.push(`/t/${cleanSlug}/settings`);
      }
    } catch (err) {
      console.error(err);
      if (err.message && err.message.startsWith('SLUG_TAKEN:')) {
        setError(`Slug "${cleanSlug}" je već zauzet.`);
      } else {
        setError('Greška pri spremanju: ' + err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async () => {
    setError('');
    setSuccess('');

    if (!teamPassword || teamPassword.length < PASSWORD_MIN_LENGTH) {
      setError(`Lozinka mora imati barem ${PASSWORD_MIN_LENGTH} znaka.`);
      return;
    }

    if (teamPassword !== confirmPassword) {
      setError('Lozinke se ne podudaraju.');
      return;
    }

    setPasswordSaving(true);
    try {
      const passwordHash = await bcrypt.hash(teamPassword, 10);

      await setDoc(doc(db, 'teams', teamId), {
        passwordEnabled: true,
        passwordHash,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await setDoc(doc(db, 'slugs', teamData.slug), {
        passwordEnabled: true,
        teamName: name.trim(),
        visibility,
      }, { merge: true });

      setPasswordEnabled(true);
      setTeamPassword('');
      setConfirmPassword('');
      setSuccess('✅ Lozinka tima je spremljena.');
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Greška pri spremanju lozinke.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleRemovePassword = async () => {
    setError('');
    setSuccess('');
    setPasswordSaving(true);

    try {
      await setDoc(doc(db, 'teams', teamId), {
        passwordEnabled: false,
        passwordHash: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await setDoc(doc(db, 'slugs', teamData.slug), {
        passwordEnabled: false,
        teamName: name.trim(),
        visibility,
      }, { merge: true });

      localStorage.removeItem(`team-unlocked:${teamData.slug}`);

      setPasswordEnabled(false);
      setTeamPassword('');
      setConfirmPassword('');
      setSuccess('✅ Lozinka tima je uklonjena.');
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Greška pri uklanjanju lozinke.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const deleteAllDocsIn = async (path) => {
    const snap = await getDocs(collection(db, path));
    for (const d of snap.docs) {
      await deleteDoc(doc(db, path, d.id));
    }
  };

  const handleDelete = async () => {
    setError('');
    if (confirmName !== teamData.name) {
      setError('Upisano ime se ne poklapa s imenom tima.');
      return;
    }

    setDeleting(true);
    try {
      await deleteAllDocsIn(`teams/${teamId}/players`);
      await deleteAllDocsIn(`teams/${teamId}/members`);
      await deleteAllDocsIn(`teams/${teamId}/matches`);

      const seasonsSnap = await getDocs(collection(db, `teams/${teamId}/seasons`));
      for (const seasonDoc of seasonsSnap.docs) {
        const seasonPath = `teams/${teamId}/seasons/${seasonDoc.id}`;
        await deleteAllDocsIn(`${seasonPath}/players`);
        await deleteAllDocsIn(`${seasonPath}/matches`);
        await deleteDoc(doc(db, 'teams', teamId, 'seasons', seasonDoc.id));
      }

      await deleteDoc(doc(db, 'teams', teamId));

      if (teamData.slug) {
        try {
          await deleteDoc(doc(db, 'slugs', teamData.slug));
        } catch (slugErr) {
          console.warn('⚠ Nisam mogao obrisati slugs/' + teamData.slug + ':', slugErr);
        }
      }

      localStorage.removeItem(`team-unlocked:${teamData.slug}`);
      history.push('/directory');
    } catch (err) {
      console.error(err);
      setError('Greška pri brisanju: ' + err.message);
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="loading-screen">Učitavanje tima…</div>;
  }

  if (!teamData) {
    return <div className="empty-state" style={{ margin: 40 }}>Tim nije pronađen.</div>;
  }

  return (
    <div className="settings-page">
      <div className="section-header">
        <h2>⚙️ Postavke tima</h2>
        <span className="section-badge">{teamData.name}</span>
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <form onSubmit={handleSave} className="settings-form">
        <div className="settings-field">
          <label>Ime tima</label>
          <input
            type="text"
            className="input-field"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            maxLength={60}
            required
          />
        </div>

        <div className="settings-field">
          <label>Slug (URL identifikator)</label>
          <div className="slug-input-wrapper">
            <span className="slug-prefix">/t/</span>
            <input
              type="text"
              value={newSlug}
              onChange={(e) => handleSlugChange(e.target.value)}
              maxLength={30}
              required
            />
          </div>
          <small style={{ color: 'var(--text-3)', fontSize: '0.8rem' }}>
            Ako promijeniš slug, stari linkovi više neće raditi.
          </small>
        </div>

        <div className="settings-field">
          <label>Opis (neobavezno)</label>
          <textarea
            className="input-field"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={300}
            placeholder="Kratak opis tima…"
          />
        </div>

        <div className="settings-field">
          <label>Logo URL (neobavezno)</label>
          <input
            type="url"
            className="input-field"
            value={logo}
            onChange={(e) => setLogo(e.target.value)}
            placeholder="https://…"
          />
        </div>

        <div className="settings-field">
          <label>Vidljivost</label>
          <div className="visibility-toggle">
            <label className={`visibility-option ${visibility === 'public' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === 'public'}
                onChange={() => setVisibility('public')}
              />
              <span className="visibility-title">🌍 Javno</span>
              <span className="visibility-desc">Tim je vidljiv u direktoriju i pristupačan svima.</span>
            </label>

            <label className={`visibility-option ${visibility === 'private' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={visibility === 'private'}
                onChange={() => setVisibility('private')}
              />
              <span className="visibility-title">🔒 Privatno</span>
              <span className="visibility-desc">Bez lozinke je skriven. Ako ukljucis lozinku, ostaje vidljiv kao zakljucan.</span>
            </label>
          </div>
        </div>

        <div className="settings-field">
          <label>Password zaštita</label>
          <div className="visibility-toggle">
            <label className={`visibility-option ${passwordEnabled ? 'selected' : ''}`}>
              <input
                type="checkbox"
                checked={passwordEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setPasswordEnabled(checked);

                  if (!checked && teamData.passwordEnabled) {
                    setError('Za uklanjanje postojeće lozinke klikni "Ukloni lozinku".');
                    setSuccess('');
                  } else {
                    setError('');
                  }
                }}
              />
              <span className="visibility-title">🔐 Zaključaj tim lozinkom</span>
              <span className="visibility-desc">
                Tim može ostati vidljiv u direktoriju, ali prije ulaska traži lozinku.
              </span>
            </label>
          </div>

          {passwordEnabled && (
            <div style={{ marginTop: 12 }}>
              <input
                type="password"
                className="input-field"
                value={teamPassword}
                onChange={(e) => setTeamPassword(e.target.value)}
                placeholder="Nova lozinka"
                style={{ marginBottom: 6 }}
              />
              <small style={{ color: 'var(--text-3)', fontSize: '0.8rem', display: 'block', marginBottom: 10 }}>
                Lozinka mora imati barem {PASSWORD_MIN_LENGTH} znaka.
              </small>
              <input
                type="password"
                className="input-field"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ponovi lozinku"
              />

              <div className="form-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSetPassword}
                  disabled={passwordSaving}
                >
                  {passwordSaving ? '⏳ Spremam lozinku…' : '🔐 Spremi lozinku'}
                </button>

                {teamData.passwordEnabled && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleRemovePassword}
                    disabled={passwordSaving}
                  >
                    Ukloni lozinku
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '⏳ Spremam…' : '💾 Spremi postavke'}
          </button>
        </div>
      </form>

      <div className="settings-section" style={{ marginTop: 32 }}>
        <h3>👥 Urednici tima</h3>
        <p style={{ color: 'var(--text-3)', fontSize: '0.88rem', marginBottom: 16 }}>
          Urednici mogu dodavati/brisati utakmice i upravljati igračima, ali ne mogu mijenjati postavke tima niti ga obrisati.
        </p>

        {managerError && <div className="form-error" style={{ marginBottom: 10 }}>{managerError}</div>}
        {managerSuccess && <div className="form-success" style={{ marginBottom: 10 }}>{managerSuccess}</div>}

        {managers.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, marginBottom: 16 }}>
            {managers.map((m) => (
              <li key={m.uid} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ flex: 1 }}>{m.label}</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleRemoveManager(m.uid)}
                >
                  Ukloni
                </button>
              </li>
            ))}
          </ul>
        )}

        {managers.length === 0 && (
          <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', marginBottom: 12 }}>
            Nema urednika. Dodaj člana tima po emailu.
          </p>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="email"
            className="input-field"
            value={managerEmail}
            onChange={(e) => setManagerEmail(e.target.value)}
            placeholder="Email člana tima"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddManager())}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddManager}
            disabled={managerLoading || !managerEmail.trim()}
          >
            {managerLoading ? '⏳' : 'Dodaj'}
          </button>
        </div>
      </div>

      <div className="danger-zone">
        <h3>⚠️ Opasna zona</h3>
        <p style={{ color: 'var(--text-3)', fontSize: '0.88rem', marginBottom: 12 }}>
          Brisanje tima je <strong>trajno</strong>. Briše tim, sve igrače i utakmice.
          Arhivirane sezone također nestaju.
        </p>

        {!showDeleteConfirm ? (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            🗑️ Obriši tim
          </button>
        ) : (
          <div className="danger-confirm">
            <p style={{ marginBottom: 10 }}>
              Za potvrdu upiši točno ime tima: <strong>{teamData.name}</strong>
            </p>
            <input
              type="text"
              className="input-field"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Upiši ime tima"
            />
            <div className="form-actions" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setConfirmName('');
                  setError('');
                }}
                disabled={deleting}
              >
                Odustani
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting || confirmName !== teamData.name}
              >
                {deleting ? '⏳ Brišem…' : 'Trajno obriši'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* eslint-enable jsx-a11y/accessible-emoji */

export default TeamSettings;
